import React from 'react';
import {
  Box,
  VStack,
  HStack,
  Heading,
  Text,
  Container,
  useColorModeValue,
  useBreakpointValue,
  Avatar,
  Badge,
  Flex,
  Icon,
  Grid,
  GridItem,
  Divider
} from '@chakra-ui/react';
import { FaServer, FaGlobe, FaTools, FaCoffee } from 'react-icons/fa';

const Pricing = () => {
  const textColor = useColorModeValue('gray.800', 'gray.100');
  const accentColor = useColorModeValue('yellow.400', 'yellow.300');
  const cardBgColor = useColorModeValue('white', 'gray.700');
  
  const headingSize = useBreakpointValue({ base: 'xl', md: '2xl' });
  const priceSize = useBreakpointValue({ base: '4xl', md: '6xl' });
  
  const costBreakdown = [
    {
      icon: FaGlobe,
      item: 'Domain Registration',
      cost: '₹800/year',
      color: 'blue.400',
      bgColor: 'blue.50'
    },
    {
      icon: FaCoffee,
      item: 'AI Engine costing',
      cost: '₹2000/month',
      color: 'orange.400',
      bgColor: 'orange.50'
    },
    {
      icon: FaServer,
      item: 'Cloud Hosting',
      cost: '₹1,200/month',
      color: 'green.400',
      bgColor: 'green.50'
    },
    {
      icon: FaTools,
      item: 'Maintenance & Updates',
      cost: '₹800/month',
      color: 'purple.400',
      bgColor: 'purple.50'
    },
  ];

  return (
    <Box minH="100vh"  position="relative" overflow="hidden">
      {/* Dollar sign decorations */}
      {/*<Box
        display={{ base: 'none', md: 'block' }}
        position="absolute"
        top="25%"
        right="15%"
        fontSize="60px"
        color="green.300"
        opacity="0.4"
        zIndex="0"
        animation="float 4s ease-in-out infinite"
        sx={{
          '@keyframes float': {
            '0%, 100%': { transform: 'translateY(0px) rotate(0deg)' },
            '50%': { transform: 'translateY(-20px) rotate(10deg)' }
          }
        }}
      >
        $
      </Box>*/}

      <Box
        display={{ base: 'none', lg: 'block' }}
        position="absolute"
        bottom="30%"
        left="5%"
        fontSize="80px"
        color="yellow.400"
        opacity="0.3"
        zIndex="0"
        animation="wiggle 5s ease-in-out infinite"
        sx={{
          '@keyframes wiggle': {
            '0%, 100%': { transform: 'rotate(0deg)' },
            '25%': { transform: 'rotate(5deg)' },
            '75%': { transform: 'rotate(-5deg)' }
          }
        }}
      >
        ₹
      </Box>

      {/* Crossed out money symbols */}
      <Box
        display={{ base: 'none', md: 'block' }}
        position="absolute"
        bottom="20%"
        right="8%"
        fontSize="50px"
        color="red.300"
        opacity="0.5"
        zIndex="0"
      >
        <Text>💰</Text>
        <Box
          position="absolute"
          top="50%"
          left="50%"
          transform="translate(-50%, -50%) rotate(45deg)"
          w="60px"
          h="4px"
          bg="red.400"
          borderRadius="full"
        />
      </Box>

      {/* Mobile decorations */}
      <Box
        display={{ base: 'block', md: 'none' }}
        position="absolute"
        top="8%"
        right="10%"
        w="40px"
        h="40px"
        bg="green.200"
        borderRadius="50%"
        opacity="0.6"
        zIndex="0"
        animation="pulse 2s ease-in-out infinite"
        sx={{
          '@keyframes pulse': {
            '0%, 100%': { transform: 'scale(1)' },
            '50%': { transform: 'scale(1.2)' }
          }
        }}
      />

      <Container
        maxW="7xl"
        position="relative"
        zIndex="1"
        minH="100vh"
        display="flex"
        alignItems="center"
        justifyContent="center"
        py={{ base: 10, md: 0 }}
      >
        <VStack spacing={{ base: 8, md: 16 }} px={{ base: 4, md: 0 }}>
          
          {/* Header Section */}
          <VStack spacing={{ base: 4, md: 6 }} textAlign="center" maxW="4xl">
            <Heading
              as="h2"
              size={headingSize}
              color={textColor}
              fontWeight="bold"
              lineHeight="1.2"
              marginBottom="10"
            >
              Simple, Transparent Pricing
            </Heading>

            {/* Big FREE Price Display */}
            <Box
              bg={cardBgColor}
              p={{ base: 8, md: 12 }}
              borderRadius="3xl"
              border="4px solid"
              borderColor={accentColor}
              boxShadow="2xl"
              position="relative"
              overflow="hidden"
            >
              {/* Decorative elements inside price card */}
              <Box
                position="absolute"
                top="-30px"
                right="-30px"
                w="100px"
                h="100px"
                bg="yellow.100"
                borderRadius="50%"
                opacity="0.5"
                animation="spin 20s linear infinite"
                sx={{
                  '@keyframes spin': {
                    '0%': { transform: 'rotate(0deg)' },
                    '100%': { transform: 'rotate(360deg)' }
                  }
                }}
              />

              <VStack spacing={4} position="relative" zIndex="1">
                <Badge
                  colorScheme="green"
                  fontSize="lg"
                  px={4}
                  py={2}
                  borderRadius="full"
                  textTransform="uppercase"
                  letterSpacing="wider"
                >
                  100% FREE
                </Badge>

                <Flex align="center" justify="center">
                  <Text
                    fontSize={priceSize}
                    fontWeight="black"
                    color="green.500"
                    textShadow="2px 2px 4px rgba(0,0,0,0.1)"
                  >
                    ₹0
                  </Text>
                </Flex>

                <Text fontSize="lg" color="gray.600" fontWeight="medium">
                  No setup fees, no monthly charges, no hidden costs
                </Text>
              </VStack>
            </Box>

            <VStack spacing={2} marginTop="10">
              <Text fontSize="lg" color="gray.700">
                But wait... who pays for all this?
              </Text>
            </VStack>
          </VStack>

          {/* Who Pays Section */}
          <Box
            bg={cardBgColor}
            p={{ base: 6, md: 8 }}
            borderRadius="2xl"
            border="2px solid"
            borderColor="orange.200"
            boxShadow="lg"
            maxW="4xl"
            w="full"
          >
            <VStack spacing={6}>
              <Heading as="h3" size="lg" color={textColor} textAlign="center">
                Meet Your Sponsor
              </Heading>

              {/* Creator Section */}
              <Flex
                direction={{ base: 'column', md: 'row' }}
                align="center"
                justify="center"
                gap={6}
                w="full"
              >
                <VStack spacing={3}>
                  {/* Replace this src with your actual photo */}
                  <Avatar
                    size="2xl"
                    src="https://avatars.githubusercontent.com/u/100429756?v=4" // Replace with your photo path
                    name="Sankalpie" // Replace with your name
                    // border="4px solid"
                    // borderColor={accentColor}
                    boxShadow="lg"
                  />
                  <Badge colorScheme="yellow" fontSize="sm" px={3} py={1} borderRadius="full">
                    sankalp@PiePOS.in
                  </Badge>
                </VStack>

                <VStack spacing={4} textAlign={{ base: 'center', md: 'left' }} flex="1">
                  <Text fontSize="lg" fontWeight="bold" color={textColor}>
                    Hi! It's me Sankalp
                  </Text>
                  <Text fontSize="md" color="gray.600" lineHeight="1.6">
                    I built PiePOS to help restaurant owners succeed without breaking the bank.
                    I noticed restaurants still run on the same principle as they did over 30 years ago.
                    I'm here to change that and make it easier for them to techify their business and grow, without the high costs.
                  </Text>
                  <HStack spacing={2}>
                    <Text fontSize="sm" color="gray.500" fontStyle="italic">
                      "Building something meaningful, one restaurant at a time"
                    </Text>
                  </HStack>
                </VStack>
              </Flex>

              <Divider borderColor="gray.200" />

              {/* Cost Breakdown */}
              <VStack spacing={4} w="full">
                <Text fontSize="lg" fontWeight="semibold" color={textColor}>
                  What We Cover For You:
                </Text>
                
                <Grid
                  templateColumns={{ base: '1fr', md: 'repeat(2, 1fr)' }}
                  gap={4}
                  w="full"
                >
                  {costBreakdown.map((item, index) => (
                    <GridItem key={index}>
                      <HStack
                        spacing={3}
                        p={4}
                        bg={item.bgColor}
                        borderRadius="xl"
                        border="1px solid"
                        borderColor={item.color}
                      >
                        <Box
                          p={2}
                          bg={item.color}
                          borderRadius="lg"
                          color="white"
                        >
                          <Icon as={item.icon} boxSize="5" />
                        </Box>
                        <VStack spacing={0} align="start" flex="1">
                          <Text fontSize="sm" fontWeight="semibold" color={textColor}>
                            {item.item}
                          </Text>
                          <Text fontSize="xs" color="gray.600">
                            {item.cost}
                          </Text>
                        </VStack>
                      </HStack>
                    </GridItem>
                  ))}
                </Grid>
              </VStack>

              <Box
                bg="yellow.50"
                p={4}
                borderRadius="xl"
                border="2px dashed"
                borderColor={accentColor}
                w="full"
              >
                <Text fontSize="sm" color="gray.700" textAlign="center" fontWeight="medium">
                  Total Monthly Investment: ~₹4,066 per restaurant x All Covered by PiePOS = <span><b>Your cost: ₹0</b></span>
                </Text>
              </Box>
            </VStack>
          </Box>

        </VStack>
      </Container>
    </Box>
  );
};

export default Pricing;